import { QueryItem } from './../../Tonnage/models/TonnageQueryModel';
import { RootState } from '../../../app/store';
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { OrderDirection } from '../../Datatable/OrderDirection';
import ContactNameModel from '../../ContactSelector/models/ContactNameModel';
import FixtureQueryModel from '../models/FixtureQueryModel';
import FixtureModel from '../models/FixtureModel';
import { } from '../../Tonnage/models/TonnageQueryModel';
import CompanyNamePICModel from '../../CompanyNamePIC/models/CompanyNamePICModel';
import Utility from '../../../app/utility';

interface fixtureSearchState {
    query: FixtureQueryModel,
    searchResults: FixtureModel[],
    companyNamePICSearchKeyword: string,
    companyNamePICData: CompanyNamePICModel[],
    contactAccountNames: ContactNameModel[],
    triggerSearch: number,
    datatableSelectedIndex: number,
    searchResultTotalRecord: number,
    checkedFixtures: FixtureModel[],
    triggerFocusVesselName: number,
}

const initialState: fixtureSearchState = {
    query: {
        cpDateFrom: "",
        cpDateTo: "",
        bizTypes: [],
        vesselNames: [],
        owners: [],
        charterers: [],
        collectFroms: [],
        supports: [],
        status: [],
        evaluations: [],
        commRateBrokers: [],
        invDateFrom: "",
        invDateTo: "",
        bankAccount: "",
        receivedDateFrom: "",
        receivedDateTo: "",
        invNo: "",
        showCloseFile: false,
        orderBy: Utility.propertyOf<FixtureModel>("cpDate"),
        orderDirection: OrderDirection.Desc,
    } as FixtureQueryModel,
    searchResults: [],
    companyNamePICSearchKeyword: "",
    companyNamePICData: [],
    contactAccountNames: [],
    triggerSearch: 0,
    datatableSelectedIndex: -1,
    searchResultTotalRecord: 0,
    checkedFixtures: [],
    triggerFocusVesselName: 0,
}

export const FixtureSearchSlice = createSlice({
    name: "FixtureSearch",
    initialState,
    reducers: {
        setFixtureSearchResults: (state, action: PayloadAction<FixtureModel[]>) => {
            state.searchResults = action.payload;
        },
        setFixtureCompanyNamePICSearchKeyword: (state, action: PayloadAction<string>) => {
            state.companyNamePICSearchKeyword = action.payload;
        },
        setTriggerFixtureSearch: (state, action: PayloadAction<number>) => {
            state.triggerSearch = action.payload;
        },
        setDatatableSelectedFixtureIndex: (state, action: PayloadAction<number>) => {
            state.datatableSelectedIndex = action.payload;
        },
        setSearchResultTotalRecord: (state, action: PayloadAction<number>) => {
            state.searchResultTotalRecord = action.payload;
        },
        setCpDateFrom: (state, action: PayloadAction<string>) => {
            state.query.cpDateFrom = action.payload;
        },
        setCpDateTo: (state, action: PayloadAction<string>) => {
            state.query.cpDateTo = action.payload;
        },
        setBizTypes: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.bizTypes = action.payload;
        },
        setVesselNames: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.vesselNames = action.payload;
        },
        setOwners: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.owners = action.payload;
        },
        setCharterers: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.charterers = action.payload;
        },
        setShowCloseFile: (state, action: PayloadAction<boolean>) => {
            state.query.showCloseFile = action.payload;
        },
        setCollectFroms: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.collectFroms = action.payload;
        },
        setStatus: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.status = action.payload;
        },
        setInvDateFrom: (state, action: PayloadAction<string>) => {
            state.query.invDateFrom = action.payload;
        },
        setInvDateTo: (state, action: PayloadAction<string>) => {
            state.query.invDateTo = action.payload;
        },
        setSupports: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.supports = action.payload;
        },
        setBankAccount: (state, action: PayloadAction<string>) => {
            state.query.bankAccount = action.payload;
        },
        setReceivedDateFrom: (state, action: PayloadAction<string>) => {
            state.query.receivedDateFrom = action.payload;
        },
        setReceivedDateTo: (state, action: PayloadAction<string>) => {
            state.query.receivedDateTo = action.payload;
        },
        setInvNo: (state, action: PayloadAction<string>) => {
            state.query.invNo = action.payload;
        },
        setEvaluations: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.evaluations = action.payload;
        },
        setCommRateBrokers: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.commRateBrokers = action.payload;
        },
        setOrderBy: (state, action: PayloadAction<string>) => {
            state.query.orderBy = action.payload;
        },
        setOrderDirection: (state, action: PayloadAction<OrderDirection>) => {
            state.query.orderDirection = action.payload;
        },
        setFixtureQuery: (state, action: PayloadAction<FixtureQueryModel>) => {
            state.query = action.payload;
        },
        resetFixtureSearch: (state) => {
            state.query = initialState.query;
        },
        setCompanyNamePICData: (state, action: PayloadAction<CompanyNamePICModel[]>) => {
            state.companyNamePICData = action.payload;
        },
        setContactAccountNames: (state, action: PayloadAction<ContactNameModel[]>) => {
            state.contactAccountNames = action.payload;
        },
        setCheckedFixtures: (state, action: PayloadAction<FixtureModel[]>) => {
            state.checkedFixtures = action.payload;
        },
        setTriggerFocusVesselName: (state, action: PayloadAction<number>) => {
            state.triggerFocusVesselName = action.payload;
        },
    }
});

export const FixtureSearchSelector = createSelector(
    (state: RootState) => state.fixtureSearch,
    (data) => data
);

export const {
    setFixtureSearchResults,
    setFixtureCompanyNamePICSearchKeyword,
    setTriggerFixtureSearch,
    setDatatableSelectedFixtureIndex,
    setSearchResultTotalRecord,
    resetFixtureSearch,
    setCompanyNamePICData,
    setCheckedFixtures,
    setTriggerFocusVesselName,
    setFixtureQuery,
    setCpDateFrom,
    setCpDateTo,
    setBizTypes,
    setVesselNames,
    setOwners,
    setCharterers,
    setEvaluations,
    setCommRateBrokers,
    setCollectFroms,
    setStatus,
    setShowCloseFile,
    setInvDateFrom,
    setInvDateTo,
    setOrderBy,
    setOrderDirection,
    setSupports,
    setBankAccount,
    setReceivedDateFrom,
    setReceivedDateTo,
    setInvNo
} = FixtureSearchSlice.actions;

export default FixtureSearchSlice.reducer;